import { Component, OnDestroy, OnInit, ViewEncapsulation, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Subscription } from 'rxjs';
import { applicationMessages, toasterMessages } from '@messages/*';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';

import { DashboardService } from '../dashboard.service';
import { LoaderService } from 'src/app/services/loader.service';
import { SnackbarComponent } from '../common/snackbar/snackbar.component';
import { TemplateService } from '../../services/template.service';

interface Template {
  _id: string;
  templateName: string;
  templateType: string;
  content?: string;
  heading?: string;
  subject?: string;
  templatePlaceholder?: string;
  isDefault?: boolean;
}

interface TabInfo {
  title: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-branding',
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss']
})
export class BrandingComponent implements OnInit, OnDestroy {
  @Input() isSubComponent: boolean = false;
  // Phone input configuration
  readonly separateDialCode = true;
  readonly SearchCountryField = SearchCountryField;
  readonly CountryISO = CountryISO;
  readonly PhoneNumberFormat = PhoneNumberFormat;
  readonly preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];

  // Template data
  emailTemplates: Template[] = [];
  welcomeTemplates: Template[] = [];
  whatsAppTemplates: Template[] = [];

  // Logo and form
  logo: string | null = null;
  fileObj: File | null = null;
  companyDetailsForm: UntypedFormGroup;

  // Template expansion state
  selectedEmailTemplateIndex: number | null = null;
  selectedWelcomeTemplateIndex: number | null = null;
  selectedWhatsAppTemplateIndex: number | null = null;

  // Tab information
  currentTabIndex = 0;
  readonly tabInfo: TabInfo[] = [
    {
      title: 'Company Details',
      description: 'Upload your company logo and complete company information',
      icon: 'business'
    },
    {
      title: 'Email Templates',
      description: 'Create and manage email templates for candidate communications',
      icon: 'email'
    },
    {
      title: 'Welcome Templates',
      description: 'Create and manage welcome templates for candidate onboarding',
      icon: 'waving_hand'
    },
    {
      title: 'WhatsApp Templates',
      description: 'Predefined templates for WhatsApp communications',
      icon: 'chat'
    }
  ];

  // Template headings
  readonly emailCardHeading = { title: 'Email Template', previewTitle: 'Email Template Preview' };
  readonly welcomeCardHeading = { title: 'Welcome Template', previewTitle: 'Welcome Template Preview' };
  readonly whatsAppCardHeading = { title: 'WhatsApp Message Template', previewTitle: 'WhatsApp Template Preview' };

  // Placeholder map for rendering variables in list headers
  headerPlaceholderDummies: { [key: string]: any } = {
    '{{candidate_name}}': 'John Smith',
    '{{CANDIDATE_NAME}}': 'John Smith',
    '{{job_title}}': 'Job Role',
    '{{JOB_TITLE}}': 'Job Role',
    '{{duration}}': 'N mins',
    '{{DURATION}}': 'N mins',
    '{{link}}': '#',
    '{{LINK}}': '#'
  };

  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialog,
    private dashboardService: DashboardService,
    private toastr: ToastrService,
    private loaderService: LoaderService,
    private fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar,
    private templateService: TemplateService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadBrandingData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Form initialization
  private initializeForm(): void {
    this.companyDetailsForm = this.fb.group({
      companyName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        this.companyNameValidator()
      ]],
      address: ['', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(200),
        this.addressValidator()
      ]],
      phone: ['', Validators.required]
    });
  }

  // Data loading
  private loadBrandingData(): void {
    this.loaderService.show('Loading branding data');
    const headers = { 'skip-loader': 'true' };

    this.subscriptions.push(
      forkJoin({
        templates: this.dashboardService.getAllTemplates({ templateUsage: 'enterprise' }, headers),
        profile: this.dashboardService.getAdminProfile(headers)
      }).subscribe({
        next: (response) => {
          this.processTemplates(response.templates);
          this.processProfile(response.profile);
          this.loaderService.hide();
        },
        error: () => {
          this.loaderService.hide();
          this.toastr.error('Failed to load branding data', 'Error');
        }
      })
    );
  }

  private processTemplates(templates: Template[]): void {
    this.emailTemplates = templates.filter(t => t.templateType === 'EMAIL_TEMPLATE');
    this.welcomeTemplates = templates.filter(t => t.templateType === 'WELCOME_TEMPLATE');
    this.whatsAppTemplates = templates.filter(t =>
      t.templateType === 'WHATSAPP_TEMPLATE' && !t.templateName.toLowerCase().includes('reminder')
    );
  }

  private processProfile(profile: any): void {
    this.logo = profile?.logo || null;
    if (profile) {
      // Try different possible field names for company name
      const companyName = profile.companyName || profile.name || profile.company_name || profile.company || '';

      // Handle phone number - new format (separate fields)
      let phoneValue: any = '';

      if (profile.phoneCountryCode && profile.phoneNumber) {
        // Clean the phone number by removing leading 0 and spaces
        const cleanPhoneNumber = profile.phoneNumber.replace(/^0+/, '').replace(/\s/g, '');

        phoneValue = {
          dialCode: profile.phoneCountryCode.replace('+', ''),
          number: cleanPhoneNumber,
          nationalNumber: cleanPhoneNumber
        };
      }

      const formData = {
        companyName: companyName,
        address: profile.address || profile.location || '',
        phone: phoneValue
      };

      // Use setValue instead of patchValue for more explicit control
      this.companyDetailsForm.setValue(formData, { emitEvent: true });

      // Build placeholder values for rendering dynamic variables in headers
      this.buildHeaderPlaceholders({
        companyName: formData.companyName,
        phone: formData.phone
      });
    }
  }

  private buildHeaderPlaceholders(ctx: { companyName: string; phone: any }): void {
    const companyName = ctx.companyName || 'Your Company';
    const phone = (typeof ctx.phone === 'object' ? (ctx.phone?.number || ctx.phone?.nationalNumber) : ctx.phone) || '91-74104 10123';

    this.headerPlaceholderDummies = {
      ...this.headerPlaceholderDummies,
      '{{company_name}}': companyName,
      '{{COMPANY_NAME}}': companyName,
      '{{enterprise_name}}': companyName,
      '{{ENTERPRISE_NAME}}': companyName,
      '{{company_phone}}': phone,
      '{{COMPANY_PHONE}}': phone
    };
  }

  // Tab management
  onTabChange(tabIndex: number): void {
    // Immediately update the tab index for responsive UI
    this.currentTabIndex = tabIndex;
  }

  // Template expansion methods
  expandEmailTemplate(index: number): void {
    this.selectedEmailTemplateIndex = this.selectedEmailTemplateIndex === index ? null : index;
  }

  expandWelcomeTemplate(index: number): void {
    this.selectedWelcomeTemplateIndex = this.selectedWelcomeTemplateIndex === index ? null : index;
  }

  expandWhatsAppTemplate(index: number): void {
    this.selectedWhatsAppTemplateIndex = this.selectedWhatsAppTemplateIndex === index ? null : index;
  }

  // Template management
  addEmailTemplate(): void {
    this.openTemplateDialog('EMAIL_TEMPLATE', 'Create Email Template');
  }

  addWelcomeTemplate(): void {
    this.openTemplateDialog('WELCOME_TEMPLATE', 'Create Welcome Template');
  }

  private openTemplateDialog(templateType: string, title: string): void {
    this.subscriptions.push(
      this.templateService.openTemplateDialog(templateType, title).subscribe(result => {
        if (result) {
          this.loadBrandingData();
          this.toastr.success(`${title} created successfully!`, 'Success');
        }
      })
    );
  }

  editTemplate(template: Template, templateName: string): void {
    // Map the template data to the format expected by CreateUpdateTemplateComponent
    const dialogData = {
      _id: template._id,
      templateName: template.templateName,
      templateType: template.templateType,
      content: template.content || '',
      heading: template.heading || template.templateName,
      update: true,
      subject: template.subject || '',
      templatePlaceholder: template.templatePlaceholder || ''
    };

    // Use the shared template service for editing
    this.subscriptions.push(
      this.templateService.openTemplateDialog(template.templateType, templateName, true, dialogData).subscribe(result => {
        if (result) {
          this.loadBrandingData();
          this.toastr.success(`${templateName} updated successfully!`, 'Success');
        }
      })
    );
  }

  deleteTemplate(template: Template, templateName: string): void {
    const isLastTemplate = (template.templateType === 'EMAIL_TEMPLATE' && this.emailTemplates.length === 1) ||
      (template.templateType === 'WELCOME_TEMPLATE' && this.welcomeTemplates.length === 1);

    if (isLastTemplate) {
      this.toastr.warning(toasterMessages.brandingDeleteTemplateWarning);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'delete-dialog-container',
      data: {
        danger: true,
        messageBody: applicationMessages.brandingDeleteTemplateConfirm.replace('{templatename}', templateName)
      }
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'confirm') {
          this.performDeleteTemplate(template, templateName);
        }
      })
    );
  }

  private performDeleteTemplate(template: Template, templateName: string): void {
    this.loaderService.show('Deleting template');
    const headers = { 'skip-loader': 'true' };

    this.subscriptions.push(
      this.dashboardService.deleteTemplate(template._id, headers).subscribe({
        next: () => {
          this.loadBrandingData();
          this.toastr.success(`${templateName} deleted successfully!`, 'Success');
          this.loaderService.hide();
        },
        error: () => {
          this.loaderService.hide();
          this.toastr.error(`Failed to delete ${templateName}`, 'Error');
        }
      })
    );
  }

  // Logo management
  triggerLogoUpload(): void {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.validateAndProcessLogo(file);
  }

  private validateAndProcessLogo(file: File): void {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      this.toastr.error('File size must be less than 5MB', 'Error');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.toastr.error('Please select a valid image file', 'Error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        if (img.width < 200 || img.height < 200) {
          this.toastr.warning(toasterMessages.brandingLogoWarning);
          return;
        }
        this.logo = e.target.result;
        this.fileObj = file;
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  // Profile update
  updateCompanyProfile(): void {
    if (this.companyDetailsForm.invalid) {
      this.toastr.error('Please fill in all required fields', 'Error');
      return;
    }

    this.loaderService.show('Updating company profile');
    const formData = new FormData();

    // Get form values
    const formValues = this.companyDetailsForm.value;

    // Normalize company name (trim and collapse multiple spaces)
    if (formValues.companyName) {
      formValues.companyName = String(formValues.companyName).replace(/\s+/g, ' ').trim();
    }

    // Normalize address while preserving new lines
    if (formValues.address) {
      const rawAddress = String(formValues.address).replace(/\r\n/g, '\n');
      formValues.address = rawAddress
        .split('\n')
        .map(line => line.replace(/[ \t]+/g, ' ').trim())
        .filter(line => line.length > 0)
        .join('\n');
    }

    // Handle phone number object from ngx-intl-tel-input
    let phoneCountryCode = '';
    let phoneNumber = '';

    if (typeof formValues.phone === 'object' && formValues.phone !== null) {
      // Extract country code and mobile number separately
      if (formValues.phone.dialCode) {
        // dialCode already contains the + symbol, so don't add another one
        phoneCountryCode = formValues.phone.dialCode.startsWith('+') ? formValues.phone.dialCode : '+' + formValues.phone.dialCode;
      }
      if (formValues.phone.number) {
        // Clean the phone number by removing leading 0 and spaces
        phoneNumber = formValues.phone.number.replace(/^0+/, '').replace(/\s/g, '');
      } else if (formValues.phone.nationalNumber) {
        // Clean the phone number by removing leading 0 and spaces
        phoneNumber = formValues.phone.nationalNumber.replace(/^0+/, '').replace(/\s/g, '');
      }
    }

    // Add form data with separate country code and phone number
    formData.append('companyName', formValues.companyName || '');
    formData.append('address', formValues.address || '');
    formData.append('phoneCountryCode', phoneCountryCode);
    formData.append('phoneNumber', phoneNumber);

    // Add logo if selected
    if (this.fileObj) {
      formData.append('logo', this.fileObj);
    }

    this.subscriptions.push(
      this.dashboardService.updateCompanyProfile(formData).subscribe({
        next: () => {
          this.toastr.success('Company profile updated successfully!', 'Success');
          // Invalidate cached profile/logo so subsequent navigations see fresh data
          this.dashboardService.clearAdminProfileCache();
          this.dashboardService.clearLogoCache();
          // Reload fresh data into the form and template previews
          this.loadBrandingData();
          this.loaderService.hide();
        },
        error: () => {
          this.loaderService.hide();
          this.toastr.error('Failed to update company profile', 'Error');
        }
      })
    );
  }

  // Utility methods
  trackByTemplateId(index: number, item: Template): string {
    return item._id;
  }

  getTemplateCount(templateType: string): number {
    switch (templateType) {
      case 'EMAIL_TEMPLATE': return this.emailTemplates.length;
      case 'WELCOME_TEMPLATE': return this.welcomeTemplates.length;
      case 'WHATSAPP_TEMPLATE': return this.whatsAppTemplates.length;
      default: return 0;
    }
  }

  // Instance method validator used during form build
  private companyNameValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const raw = (control?.value ?? '').toString();
      const value = raw.trim();

      if (isOnlyWhitespace(raw)) { return { whitespace: true } as any; }
      if (value.length < 2) { return { minlengthStrict: true } as any; }
      if (/^\d+$/.test(value)) { return { digitsOnly: true } as any; }
      if (!/[A-Za-z]/.test(value)) { return { alphaRequired: true } as any; }
      if (hasConsecutiveSpaces(value)) { return { multiSpace: true } as any; }
      if (!/^[A-Za-z0-9 .,&'\-()]+$/.test(value)) { return { invalidChars: true } as any; }
      return null;
    };
  }

  // Address validator enforces trimmed length, allowed characters and non-whitespace
  private addressValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const raw = (control?.value ?? '').toString();
      const value = raw.trim();

      if (isOnlyWhitespace(raw)) { return { whitespace: true } as any; }
      if (value.length < 5) { return { minlengthStrict: true } as any; }
      if (hasConsecutiveSpaces(value)) { return { multiSpace: true } as any; }
      // Require at least one alphanumeric character
      if (!/[A-Za-z0-9]/.test(value)) { return { alnumRequired: true } as any; }
      // Allow common address characters + line breaks (\r?\n). Include colon ':'
      if (!/^[A-Za-z0-9 .,#&'\-()\/:\r\n]+$/.test(value)) { return { invalidChars: true } as any; }
      return null;
    };
  }
}

// Validators
export function isOnlyWhitespace(value: string): boolean {
  return /^\s*$/.test(value || '');
}

export function hasConsecutiveSpaces(value: string): boolean {
  // Treat multiple spaces or tabs as invalid, but allow line breaks (\r?\n)
  const normalized = (value || '').replace(/\r\n/g, "\n");
  return /[ \t]{2,}/.test(normalized);
}

